import React from 'react';
import ReactDOM from 'react-dom';
import { Button, Calendar, Input, Select, message } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import moment from 'moment';

const SUPPORTED_FORMATS = [
  'YYYY/MM/DD',
  'YYYY-M-D',
  'YYYY-MM-DD',
  'YYYYMMDD',
  'YY/MM/DD',
  'YY-M-D',
  'YY-MM-DD',
  'YYMMDD',
];

const MONTH_OPTIONS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const buildYearOptions = () => {
  const currentYear = moment().year();
  const startYear = 1900;
  const endYear = currentYear + 1;
  const years = [];
  for (let year = endYear; year >= startYear; year -= 1) {
    years.push({ label: String(year), value: year });
  }
  return years;
};

const YEAR_OPTIONS = buildYearOptions();

const normalizeDateText = (raw) =>
  String(raw ?? '')
    .trim()
    .replace(/[．。]/g, '.')
    .replace(/[／]/g, '/')
    .replace(/[年月]/g, '/')
    .replace(/日/g, '')
    .replace(/[.]/g, '-')
    .replace(/\//g, '-')
    .replace(/\s+/g, '');

const parseFlexibleDate = (raw) => {
  const normalized = normalizeDateText(raw);
  if (!normalized) {
    return null;
  }

  return moment(normalized, SUPPORTED_FORMATS, true);
};

export default class PasteableDatePicker extends React.Component {
  constructor(props) {
    super(props);
    const currentValue = props.value && moment.isMoment(props.value) ? props.value.clone() : null;
    this.state = {
      overlayVisible: false,
      inputText: currentValue ? currentValue.format('YYYY-MM-DD') : '',
      draftValue: currentValue,
      panelMode: 'month',
    };
  }

  componentDidUpdate(prevProps) {
    const prevValue = prevProps.value && moment.isMoment(prevProps.value) ? prevProps.value.format('YYYY-MM-DD') : '';
    const nextValue = this.props.value && moment.isMoment(this.props.value) ? this.props.value.format('YYYY-MM-DD') : '';

    if (prevValue !== nextValue) {
      const currentValue = this.props.value && moment.isMoment(this.props.value) ? this.props.value.clone() : null;
      this.setState({
        inputText: nextValue,
        draftValue: currentValue,
      });
    }
  }

  emitChange = (nextMoment) => {
    const formatted = nextMoment ? nextMoment.format('YYYY-MM-DD') : '';
    this.setState({
      inputText: formatted,
      draftValue: nextMoment ? nextMoment.clone() : null,
    });
    this.props.onChange?.(nextMoment, formatted);
  };

  commitInputValue = (raw) => {
    const parsed = parseFlexibleDate(raw);
    if (!parsed || !parsed.isValid()) {
      message.warning('日付は YYYY/MM/DD, YYYY-MM-DD, YYYYMMDD, YY/MM/DD, YY-MM-DD, YYMMDD で入力してください');
      const fallbackText = this.props.value && moment.isMoment(this.props.value)
        ? this.props.value.format('YYYY-MM-DD')
        : '';
      this.setState({ inputText: fallbackText });
      return false;
    }

    this.emitChange(parsed);
    return true;
  };

  handleCalendarIconPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const activeElement = document.activeElement;
    if (activeElement && typeof activeElement.blur === 'function') {
      activeElement.blur();
    }

    this.openOverlay();
  };

  openOverlay = () => {
    const baseValue = this.state.draftValue
      || (this.props.value && moment.isMoment(this.props.value) ? this.props.value.clone() : null)
      || moment();

    this.setState({
      overlayVisible: true,
      draftValue: baseValue.clone(),
      panelMode: 'month',
    });
  };

  closeOverlay = () => {
    this.setState({ overlayVisible: false });
  };

  handleInputChange = (e) => {
    this.setState({ inputText: e.target.value });
  };

  handleInputBlur = () => {
    const { inputText } = this.state;
    if (!String(inputText).trim()) {
      return;
    }
    this.commitInputValue(inputText);
  };

  handleInputPressEnter = () => {
    this.commitInputValue(this.state.inputText);
  };

  handlePaste = (e) => {
    e.preventDefault();
    const raw = e.clipboardData.getData('text');
    this.commitInputValue(raw);
  };

  handleApply = () => {
    const { draftValue } = this.state;
    if (!draftValue || !draftValue.isValid()) {
      message.warning('カレンダーから日付を選択してください');
      return;
    }

    this.emitChange(draftValue);
    this.closeOverlay();
  };

  handleCalendarPanelChange = (value, mode) => {
    this.setState({
      draftValue: value.clone(),
      panelMode: mode,
    });
  };

  renderCalendarHeader = ({ value, type, onChange, onTypeChange }) => (
    <div className="portfolio-date-calendar-header">
      <Select
        className="portfolio-date-calendar-year-select"
        value={value.year()}
        onChange={(year) => onChange(value.clone().year(year))}
        options={YEAR_OPTIONS}
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
      />
      <Select
        className="portfolio-date-calendar-month-select"
        value={value.month()}
        onChange={(month) => onChange(value.clone().month(month))}
        options={MONTH_OPTIONS.map((label, index) => ({ label, value: index }))}
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
      />
    </div>
  );

  renderOverlay() {
    if (!this.state.overlayVisible) {
      return null;
    }

    return ReactDOM.createPortal(
      <div className="portfolio-date-overlay">
        <div
          className="portfolio-date-overlay-backdrop"
          onClick={this.closeOverlay}
        />
        <div className="portfolio-date-overlay-panel">
          <div className="portfolio-date-overlay-header">
            <div className="portfolio-date-overlay-title">日付を選択</div>
            <div className="portfolio-date-overlay-value">
              {this.state.draftValue ? this.state.draftValue.format('YYYY-MM-DD') : ''}
            </div>
          </div>
          <Calendar
            fullscreen
            mode={this.state.panelMode}
            value={this.state.draftValue || moment()}
            onPanelChange={this.handleCalendarPanelChange}
            onSelect={(value) => this.setState({ draftValue: value.clone() })}
            headerRender={this.renderCalendarHeader}
            dateFullCellRender={(value) => {
              const isWeekend = value.day() === 0 || value.day() === 6;
              return (
                <div className={`portfolio-date-cell${isWeekend ? ' is-weekend' : ''}`}>
                  {value.date()}
                </div>
              );
            }}
          />
          <div className="portfolio-date-overlay-actions">
            <Button size="large" onClick={this.closeOverlay}>
              キャンセル
            </Button>
            <Button type="primary" size="large" onClick={this.handleApply}>
              適用
            </Button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  render() {
    const { style, className, placeholder = 'YYYY-MM-DD', ...rest } = this.props;

    return (
      <>
        <div className={`portfolio-date-input-wrap ${className || ''}`.trim()} style={style}>
          <Input
            {...rest}
            value={this.state.inputText}
            placeholder={placeholder}
            onChange={this.handleInputChange}
            onBlur={this.handleInputBlur}
            onPressEnter={this.handleInputPressEnter}
            onPaste={this.handlePaste}
            suffix={
              <span
                className="portfolio-date-calendar-trigger"
                onPointerDown={this.handleCalendarIconPointerDown}
              >
                <CalendarOutlined />
              </span>
            }
          />
        </div>
        {this.renderOverlay()}
      </>
    );
  }
}
